import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/* Per-user booking links — Sprint 16a. The `calendar_slot` block picks
 *  from this list at template-edit time. `{{calendar_url}}` resolves
 *  server-side to the default link at send time. */

export interface BookingLink {
  id: string;
  org_id: string;
  user_id: string;
  label: string;
  url: string;
  is_default: boolean;
  sort_order: number;
  /** Set when the link was copied from JobTetris (JT1-1919): it's edited there, not in Engage. */
  jt_event_type_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface BookingLinkWritable {
  label: string;
  url: string;
  is_default?: boolean;
  sort_order?: number;
}

export function useBookingLinks() {
  return useQuery<{ data: BookingLink[] }>({
    queryKey: ['booking-links'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: BookingLink[] }>('/booking-links');
      return data;
    },
  });
}

export function useCreateBookingLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: BookingLinkWritable) => {
      const { data } = await apiClient.post<{ data: BookingLink }>('/booking-links', payload);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['booking-links'] }),
  });
}

export function useUpdateBookingLink(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<BookingLinkWritable>) => {
      const { data } = await apiClient.patch<{ data: BookingLink }>(`/booking-links/${id}`, payload);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['booking-links'] }),
  });
}

export function useDeleteBookingLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/booking-links/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['booking-links'] }),
  });
}
